import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { KeyRound, Loader2 } from "lucide-react";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";
import { toast } from "sonner";

export function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const reset = () => setForm({ current_password: "", new_password: "", confirm: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(form.new_password)) return toast.error("Password must be at least 8 characters and include both letters and numbers");
    if (form.new_password !== form.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated");
      setOpen(false); reset();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="change-password-btn" title="Change password">
          <KeyRound className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Current password</Label>
            <PasswordInput required autoComplete="current-password" value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="rounded-sm" data-testid="cp-current" />
          </div>
          <div className="space-y-2">
            <Label>New password <span className="text-muted-foreground text-xs">(min 8, letters &amp; numbers)</span></Label>
            <PasswordInput required autoComplete="new-password" value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="rounded-sm" data-testid="cp-new" />
            <PasswordStrength value={form.new_password} />
          </div>
          <div className="space-y-2">
            <Label>Confirm new password</Label>
            <PasswordInput required autoComplete="new-password" value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="rounded-sm" data-testid="cp-confirm" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-sm" data-testid="cp-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Update password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
